import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTenantAuth } from '../context/TenantAuthContext';
import { tenantPortalAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Home,
  LogOut,
  Download,
  FileText,
  AlertTriangle,
  Plus,
  Loader2,
  Building2,
  MapPin,
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';

const CATEGORY_LABELS = {
  sanitaer: 'Sanitär',
  elektrik: 'Elektrik',
  heizung: 'Heizung',
  fenster_tueren: 'Fenster & Türen',
  dach: 'Dach',
  fassade: 'Fassade',
  wasserschaden: 'Wasserschaden',
  schimmel: 'Schimmel',
  aufzug: 'Aufzug',
  sonstiges: 'Sonstiges',
};

const PRIORITY_LABELS = {
  niedrig: 'Niedrig',
  mittel: 'Mittel',
  hoch: 'Hoch',
  dringend: 'Dringend',
};

const STATUS_LABELS = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  behoben: 'Behoben',
};

const STATUS_COLORS = {
  offen: 'bg-red-100 text-red-700',
  in_bearbeitung: 'bg-amber-100 text-amber-700',
  behoben: 'bg-emerald-100 text-emerald-700',
};

const PRIORITY_COLORS = {
  niedrig: 'bg-slate-100 text-slate-700',
  mittel: 'bg-blue-100 text-blue-700',
  hoch: 'bg-amber-100 text-amber-700',
  dringend: 'bg-red-100 text-red-700',
};

export default function MieterDashboard() {
  const { tenantUser, logout } = useTenantAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [billings, setBillings] = useState([]);
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'sonstiges',
    priority: 'mittel',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [meRes, billingsRes, damagesRes] = await Promise.all([
        tenantPortalAPI.getMe(),
        tenantPortalAPI.getBillings(),
        tenantPortalAPI.getDamages(),
      ]);
      setInfo(meRes.data);
      setBillings(billingsRes.data);
      setDamages(damagesRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/mieter/login');
  };

  const handleDownloadPdf = async (billingId) => {
    try {
      const response = await tenantPortalAPI.downloadBillingPdf(billingId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Nebenkostenabrechnung_${billingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Abrechnung kann nur als finalisierte Version heruntergeladen werden.');
    }
  };

  const handleSubmitDamage = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await tenantPortalAPI.createDamage({
        unit_id: info?.unit?.id,
        ...formData,
      });
      toast.success('Schaden gemeldet');
      setDialogOpen(false);
      setFormData({ title: '', description: '', category: 'sonstiges', priority: 'mittel' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Melden');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mieter-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Mieterportal
              </h1>
              <p className="text-xs text-slate-500">{tenantUser?.tenant_name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            data-testid="mieter-logout"
            onClick={handleLogout}
            className="text-slate-600 hover:text-slate-900"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Welcome */}
        <div>
          <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Hallo {tenantUser?.tenant_name}
          </h2>
          <p className="text-slate-500 mt-1">Willkommen in Ihrem Mieterportal</p>
        </div>

        {/* Property Info */}
        {info && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Objekt</p>
                    <p className="font-medium text-slate-900">{info.property?.name}</p>
                    <p className="text-sm text-slate-600">Einheit: {info.unit?.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Adresse</p>
                    <p className="font-medium text-slate-900">{info.property?.address}</p>
                    <p className="text-sm text-slate-600">
                      {info.property?.postal_code} {info.property?.city}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="billings" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="billings" data-testid="tab-billings">
              <FileText className="h-4 w-4 mr-2" />
              Abrechnungen
            </TabsTrigger>
            <TabsTrigger value="damages" data-testid="tab-damages">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Schäden
            </TabsTrigger>
          </TabsList>

          {/* Billings */}
          <TabsContent value="billings" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Meine Nebenkostenabrechnungen</CardTitle>
              </CardHeader>
              <CardContent>
                {billings.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Noch keine Abrechnungen vorhanden</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {billings.map((billing) => (
                      <div
                        key={billing.id}
                        data-testid={`billing-row-${billing.id}`}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-slate-200 rounded-lg gap-3"
                      >
                        <div>
                          <p className="font-medium text-slate-900">
                            Zeitraum: {formatDate(billing.period_start)} – {formatDate(billing.period_end)}
                          </p>
                          <div className="flex gap-4 mt-1 text-sm text-slate-600">
                            <span>Anteil: {formatCurrency(billing.tenant_share)}</span>
                            <span>Vorauszahlung: {formatCurrency(billing.prepayments)}</span>
                            <span
                              className={`font-medium ${
                                billing.balance > 0 ? 'text-red-600' : 'text-emerald-600'
                              }`}
                            >
                              {billing.balance > 0
                                ? `Nachzahlung: ${formatCurrency(billing.balance)}`
                                : `Guthaben: ${formatCurrency(Math.abs(billing.balance))}`}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            className={
                              billing.status === 'final'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-700'
                            }
                          >
                            {billing.status === 'final' ? 'Final' : 'Entwurf'}
                          </Badge>
                          {billing.status === 'final' && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`download-billing-${billing.id}`}
                              onClick={() => handleDownloadPdf(billing.id)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Damages */}
          <TabsContent value="damages" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Schäden in Ihrer Einheit</CardTitle>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="report-damage-button"
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Schaden melden
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Schaden melden</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitDamage} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">
                          Titel <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="title"
                          data-testid="report-title-input"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="z.B. Wasserhahn tropft"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Kategorie</Label>
                          <Select
                            value={formData.category}
                            onValueChange={(v) => setFormData({ ...formData, category: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                <SelectItem key={v} value={v}>
                                  {l}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Priorität</Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(v) => setFormData({ ...formData, priority: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(PRIORITY_LABELS).map(([v, l]) => (
                                <SelectItem key={v} value={v}>
                                  {l}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">
                          Beschreibung <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="description"
                          data-testid="report-description-input"
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({ ...formData, description: e.target.value })
                          }
                          placeholder="Detaillierte Beschreibung des Schadens..."
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          type="submit"
                          disabled={saving}
                          data-testid="report-submit-button"
                          className="bg-slate-900 hover:bg-slate-800"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Senden...
                            </>
                          ) : (
                            'Schaden melden'
                          )}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {damages.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Keine Schäden gemeldet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {damages.map((damage) => (
                      <div
                        key={damage.id}
                        data-testid={`damage-row-${damage.id}`}
                        className="p-4 border border-slate-200 rounded-lg"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{damage.title}</p>
                            <p className="text-sm text-slate-600 mt-1">{damage.description}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              Gemeldet am {formatDate(damage.reported_date)} von {damage.reported_by}
                            </p>
                            {damage.resolution_notes && (
                              <p className="text-sm text-slate-700 mt-2 p-2 bg-slate-50 rounded">
                                <span className="font-medium">Hinweis vom Vermieter:</span>{' '}
                                {damage.resolution_notes}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Badge className={PRIORITY_COLORS[damage.priority]}>
                              {PRIORITY_LABELS[damage.priority]}
                            </Badge>
                            <Badge className={STATUS_COLORS[damage.status]}>
                              {STATUS_LABELS[damage.status]}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-6 mt-8 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-2">
        <Link to="/impressum" className="hover:text-slate-900">Impressum</Link>
        <Link to="/datenschutz" className="hover:text-slate-900">Datenschutz</Link>
        <Link to="/agb" className="hover:text-slate-900">AGB</Link>
        <span className="ml-auto">© {new Date().getFullYear()} Kivera</span>
      </footer>
    </div>
  );
}
